import type { FirebaseApp } from 'firebase/app';
import { getRemoteConfig, fetchAndActivate, getValue, type RemoteConfig } from 'firebase/remote-config';
import { REMOTE_CONFIG_DEFAULTS } from '../../config/remoteConfigDefaults';
import { useFirebaseEmulators, isProductionEnv } from '../../config/env';

let remoteConfig: RemoteConfig | null = null;

function readDefault(key: string): string | number | boolean | undefined {
  return REMOTE_CONFIG_DEFAULTS[key as keyof typeof REMOTE_CONFIG_DEFAULTS];
}

export async function initRemoteConfig(app: FirebaseApp) {
  if (useFirebaseEmulators()) {
    return { source: 'defaults', active: true };
  }

  remoteConfig = getRemoteConfig(app);
  remoteConfig.defaultConfig = REMOTE_CONFIG_DEFAULTS;
  remoteConfig.settings.minimumFetchIntervalMillis = isProductionEnv()
    ? 3_600_000
    : 0;

  try {
    await fetchAndActivate(remoteConfig);
    return { source: 'remote', active: true };
  } catch (err) {
    console.warn('[RemoteConfig] fetch failed, using defaults:', err);
    return { source: 'defaults', active: false };
  }
}

export function getConfigString(key: string, fallback = '') {
  const defaultValue = readDefault(key);
  const defaultString = defaultValue != null ? String(defaultValue) : fallback;
  if (!remoteConfig || useFirebaseEmulators()) {
    return defaultString;
  }
  return getValue(remoteConfig, key).asString() || defaultString;
}

export function getConfigBoolean(key: string, fallback = false) {
  const defaultValue = readDefault(key);
  const defaultBoolean = typeof defaultValue === 'boolean' ? defaultValue : fallback;
  if (!remoteConfig || useFirebaseEmulators()) {
    return defaultBoolean;
  }
  const value = getValue(remoteConfig, key).asBoolean();
  return value ?? defaultBoolean;
}

export function getConfigNumber(key: string, fallback = 0) {
  const defaultValue = readDefault(key);
  const defaultNumber = typeof defaultValue === 'number' ? defaultValue : fallback;
  if (!remoteConfig || useFirebaseEmulators()) {
    return defaultNumber;
  }
  const value = getValue(remoteConfig, key).asNumber();
  return Number.isFinite(value) ? value : defaultNumber;
}
